import { FC, useMemo } from 'react';
import { IonItem, IonLabel } from '@ionic/react';
import Button from '../../../../Button/Button';
import ObjectNavigator from '../ObjectNavigator/ObjectNavigator';
import SettingsPanel from '../SettingsPanel/SettingsPanel';
import { useEditorPanelHandlers } from '../../hooks/useEditorPanelHandlers';
import { useTranslate } from '../../../../../hooks/context/useTranslate';
import { useDatasetContext } from '../../../../../hooks/context/useDatasetContext';
import { useLayoutEditorContext } from '../../../../../hooks/context/useLayoutEditorContext';
import { objectToFormData } from '../../../../../utils/layouts/objectToFormData';
import {
  LAYOUT_FIXED_MODE_DISABLED_FIELDS,
  LAYOUT_LOCKED_ITEM_FIELDS,
} from '../../../../../constants';
import { FullscreenReferenceType } from '../../../../../types/types';

interface EditorObjectFormProps {
  onOpenFullscreenReference: (reference: FullscreenReferenceType) => void;
}

// Content of the "Objekt" accordion section (header + content slots, no
// accordion tag): navigator + the definition-driven form of the selected
// object + the delete action. Everything context-derived (incl. the glue
// handlers) is consumed here.
const EditorObjectForm: FC<EditorObjectFormProps> = ({
  onOpenFullscreenReference,
}) => {
  const { t } = useTranslate();
  const { referenceDatasets, setReferenceDatasets } = useDatasetContext();
  const {
    definition,
    canvas,
    objects,
    selectedIndex,
    isFixedMode,
    saveErrors,
  } = useLayoutEditorContext();
  const {
    handleDeleteObject,
    handleObjectFieldChange,
    handleObjectFieldDefault,
    handleObjectFieldFocus,
    handleObjectFieldBlur,
  } = useEditorPanelHandlers();

  const selectedObject =
    selectedIndex === null ? undefined : objects[selectedIndex];

  // removeObject is a no-op only while the layout arrangement is locked;
  // deleting works in fixed mode too (the grid reflows by index).
  const isDeleteDisabled = !!canvas.lockItems;

  const objectFormData = useMemo(
    () =>
      selectedObject && definition
        ? objectToFormData(selectedObject, definition.objectForm)
        : {},
    [selectedObject, definition]
  );

  // lockItems disables the editor-specific item fields; fixed mode disables the
  // geometry on top (the grid cell derives from the array index).
  const disabledObjectFields = useMemo(() => {
    const fields = [
      ...(canvas.lockItems ? LAYOUT_LOCKED_ITEM_FIELDS : []),
      ...(isFixedMode ? LAYOUT_FIXED_MODE_DISABLED_FIELDS : []),
    ];

    return fields.length > 0 ? Array.from(new Set(fields)) : undefined;
  }, [canvas.lockItems, isFixedMode]);

  // Save-validation errors rendered under the offending inputs.
  const getObjectFieldSaveError = (name: string): string | null =>
    saveErrors.find(
      (saveError) =>
        saveError.scope === 'object' &&
        saveError.index === selectedIndex &&
        saveError.field === name
    )?.errorKey ?? null;

  if (!definition) {
    return null;
  }

  return (
    <>
      <IonItem
        className="layout-editor-panel-section-header"
        slot="header"
        lines="none"
      >
        <IonLabel>{t('layoutEditor.objectSection')}</IonLabel>
      </IonItem>
      <div slot="content">
        {selectedObject ? (
          <>
            <ObjectNavigator />
            <SettingsPanel
              key={selectedObject.feKey}
              form={definition.objectForm}
              data={objectFormData}
              referenceDatasets={referenceDatasets}
              setReferenceDatasets={setReferenceDatasets}
              disabledFields={disabledObjectFields}
              getFieldSaveError={getObjectFieldSaveError}
              handlerScope="object"
              onFieldChange={handleObjectFieldChange}
              onFieldDefault={handleObjectFieldDefault}
              onFieldFocus={handleObjectFieldFocus}
              onFieldBlur={handleObjectFieldBlur}
              onOpenFullscreenReference={onOpenFullscreenReference}
            />
            <Button
              fullWidth
              color="danger"
              className="layout-editor-panel-action"
              disabled={isDeleteDisabled}
              onClick={handleDeleteObject}
            >
              {t('layoutEditor.deleteObject')}
            </Button>
          </>
        ) : (
          <p className="layout-editor-panel-hint">
            {t('layoutEditor.noObjectSelected')}
          </p>
        )}
      </div>
    </>
  );
};

export default EditorObjectForm;
